import { useTranslation } from 'react-i18next';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  OpenMaskOverride,
  useParameterizedOpenMask,
} from '../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { useStringifyTableGroupFilter } from '../../hooks';
import { OrganisationArea } from '../organisation-area';

import { EntityTableV8, IEntityTableV8 } from './entity-table/EntityTableV8';
import { useEntityDataTableV8 } from './entity-table/use-entity-data-table-v8';
import { useFileActions } from './FileActions';
import schema from './schemata/customer-table-schema.json';

type ICustomerTableCustomColumns = Record<string, never>;

const defaultSort = [{ field: 'name', direction: SortDirection.ASCENDING }];

export function CustomersTable(props: IEntityTableV8) {
  const { entity: entityType } = schema as never;

  const dataTable = useEntityDataTableV8<Customer, ICustomerTableCustomColumns>(
    {
      ...props,
      schema: schema as never,
      defaultSort,
      rowModifiers: (value) => {
        return { isFaded: value.extinct === -1 };
      },
    }
  );

  const { fileActionDefinitions, openInNewTab } = useFileActions({
    entity: entityType,
    table: dataTable.table,
  });

  const { t } = useTranslation();

  const presetFields = useStringifyTableGroupFilter<Customer>({
    groupKey: 'group',
    groupIdKey: 'groupId',
    table: dataTable.table,
  });

  const onOpenMask = useParameterizedOpenMask({
    entityType: Entities.customer,
    params: { create: { presetFields } },
  });

  return (
    <OpenMaskOverride value={{ onOpenMask }}>
      <EntityTableV8
        {...props}
        {...dataTable}
        areas={{
          left: {
            title: t('COMMON.ORGANISATION'),
            content: (
              <OrganisationArea entity={entityType} table={dataTable.table} />
            ),
            resizable: true,
            collapsible: true,
            custom: true,
          },
        }}
        actions={{
          custom: {
            left: fileActionDefinitions,
          },
          openInNewTab,
          createWidget: false,
        }}
      />
    </OpenMaskOverride>
  );
}
